/**
 * scrub.ts — Sentry event PII scrubber for Veterans Shop.
 *
 * T7.S26 (2026-05-11). Mirrors the design in `veterans/web/src/lib/sentry/scrub.ts`
 * but trimmed to the regulated identifiers that can flow through the storefront
 * surface (shipping address, customer email, Stripe metadata, Printify order
 * payloads). Single source of truth for the `beforeSend` hooks wired into
 * `sentry.{client,server,edge}.config.ts`.
 *
 * Guarantees:
 *   - SSN dashed / labeled / unspaced — redacted
 *   - DD-214 reference + ~80 chars of trailing context — redacted
 *   - Email addresses — redacted in free text, dropped from event.user
 *   - IP addresses + usernames — dropped from event.user
 *   - Sensitive object keys (password, token, secret, authorization, cookie,
 *     ssn, dd214, file_number, va_file_number, dob, card_number, cvv, email)
 *     — replaced with `[redacted]` regardless of value shape
 *   - Recursive walk capped at depth 5 to prevent stack blow from circular
 *     React component trees Sentry sometimes captures
 *   - Cycle detection via WeakSet
 *   - event.user.id hashed (never raw email or login)
 */

// ─── Regex set ───────────────────────────────────────────────────────────────

const SSN_DASHED_RE = /\b\d{3}-\d{2}-\d{4}\b/g
const SSN_SPACED_RE = /\b\d{3}\s\d{2}\s\d{4}\b/g
const SSN_LABELED_RE = /\bSSN[: ]\s*\d{9}\b/gi
const DD214_RE = /\bDD-?214\b[^\n\r]{0,80}/gi
const EMAIL_RE = /[\w.+-]+@[\w-]+\.[\w.-]+/g
// Common card prefix patterns — non-authoritative; we mainly rely on key-name
// redaction (card_number/cvv) for PCI-relevant fields, this is belt+suspenders
// for stray strings.
const CARD_RE = /\b(?:\d[ -]?){13,19}\b/g

const SENSITIVE_KEY_SUBSTRINGS = ['password', 'token', 'secret', 'email'] as const

const SENSITIVE_KEY_EXACT = new Set([
  'authorization',
  'cookie',
  'ssn',
  'social_security',
  'social_security_number',
  'dd214',
  'dd_214',
  'discharge_papers',
  'file_number',
  'va_file_number',
  'dob',
  'date_of_birth',
  'card_number',
  'cvv',
])

const REDACT_PLACEHOLDER = '[redacted]'
const MAX_DEPTH = 5

// ─── Public helpers ──────────────────────────────────────────────────────────

export function scrubString(input: string): string {
  if (!input) return input
  return input
    .replace(SSN_LABELED_RE, '[ssn_redacted]')
    .replace(SSN_DASHED_RE, '[ssn_redacted]')
    .replace(SSN_SPACED_RE, '[ssn_redacted]')
    .replace(DD214_RE, '[dd214_redacted]')
    .replace(EMAIL_RE, '[email_redacted]')
    .replace(CARD_RE, (match) => {
      // Only redact if it's a plausible card number length after stripping
      // separators — avoids redacting order-id numerals or zip+4 sequences.
      const digits = match.replace(/[\s-]/g, '')
      return digits.length >= 13 && digits.length <= 19 ? '[card_redacted]' : match
    })
}

function isSensitiveKey(key: string): boolean {
  const lower = key.toLowerCase()
  if (SENSITIVE_KEY_EXACT.has(lower)) return true
  for (const sub of SENSITIVE_KEY_SUBSTRINGS) {
    if (lower.includes(sub)) return true
  }
  return false
}

export function scrubValue(value: unknown, depth = 0, seen = new WeakSet<object>()): unknown {
  if (depth >= MAX_DEPTH) return REDACT_PLACEHOLDER
  if (value === null || value === undefined) return value
  if (typeof value === 'string') return scrubString(value)
  if (typeof value === 'number' || typeof value === 'boolean') return value
  if (typeof value !== 'object') return value

  if (seen.has(value as object)) return REDACT_PLACEHOLDER
  seen.add(value as object)

  if (Array.isArray(value)) {
    return value.map((v) => scrubValue(v, depth + 1, seen))
  }

  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    if (isSensitiveKey(k)) {
      out[k] = REDACT_PLACEHOLDER
    } else {
      out[k] = scrubValue(v, depth + 1, seen)
    }
  }
  return out
}

/**
 * hashUserId — stable non-cryptographic hash (FNV-1a 32-bit). Sentry only
 * needs a stable correlation key, never the raw email or user id.
 */
export function hashUserId(raw: string | undefined | null): string {
  if (!raw) return 'anon'
  let hash = 0x811c9dc5
  for (let i = 0; i < raw.length; i++) {
    hash ^= raw.charCodeAt(i)
    hash = Math.imul(hash, 0x01000193)
  }
  return `u_${(hash >>> 0).toString(36)}`
}

// ─── Sentry event entry point ────────────────────────────────────────────────

type SentryEvent = {
  message?: string
  exception?: { values?: Array<{ value?: string; [k: string]: unknown }> }
  breadcrumbs?: Array<{ message?: string; data?: Record<string, unknown>; [k: string]: unknown }>
  extra?: Record<string, unknown>
  contexts?: Record<string, unknown>
  tags?: Record<string, unknown>
  request?: {
    headers?: Record<string, unknown>
    cookies?: unknown
    data?: unknown
    [k: string]: unknown
  }
  user?: { id?: string; email?: string; ip_address?: string; username?: string; [k: string]: unknown }
  [k: string]: unknown
}

export function scrubEvent(event: SentryEvent): SentryEvent {
  if (event.user) {
    const rawId = event.user.id || event.user.email || event.user.username
    event.user = {
      id: hashUserId(typeof rawId === 'string' ? rawId : undefined),
    }
  }

  if (typeof event.message === 'string') {
    event.message = scrubString(event.message)
  }

  if (event.exception?.values) {
    event.exception.values = event.exception.values.map((ex) => ({
      ...ex,
      value: typeof ex.value === 'string' ? scrubString(ex.value) : ex.value,
    }))
  }

  if (Array.isArray(event.breadcrumbs)) {
    event.breadcrumbs = event.breadcrumbs.map((bc) => ({
      ...bc,
      message: typeof bc.message === 'string' ? scrubString(bc.message) : bc.message,
      data: bc.data ? (scrubValue(bc.data) as Record<string, unknown>) : bc.data,
    }))
  }

  if (event.extra) {
    event.extra = scrubValue(event.extra) as Record<string, unknown>
  }

  if (event.contexts) {
    event.contexts = scrubValue(event.contexts) as Record<string, unknown>
  }

  if (event.tags) {
    event.tags = scrubValue(event.tags) as Record<string, unknown>
  }

  if (event.request) {
    if (event.request.cookies) event.request.cookies = REDACT_PLACEHOLDER
    if (event.request.headers) {
      event.request.headers = scrubValue(event.request.headers) as Record<string, unknown>
    }
    if (event.request.data) {
      event.request.data = scrubValue(event.request.data)
    }
  }

  return event
}

// ─── denyUrls + ignoreErrors ─────────────────────────────────────────────────

export const denyUrls: RegExp[] = [
  /^chrome-extension:\/\//i,
  /^moz-extension:\/\//i,
  /^safari-web-extension:\/\//i,
  /^webkit-masked-url:/i,
  /googletagmanager\.com/i,
  /google-analytics\.com/i,
  /^https?:\/\/localhost[:/]/i,
]

export const ignoreErrors: Array<string | RegExp> = [
  'top.GLOBALS',
  /extension\/.*/i,
  /ResizeObserver loop limit exceeded/i,
  /ResizeObserver loop completed with undelivered notifications/i,
  'NetworkError when attempting to fetch resource',
  /Load failed/i,
  'AbortError',
  /The user aborted a request/i,
]
